import React, { useCallback, useEffect, useState } from 'react';
import { ChevronRight, File as FileIcon, Folder, FolderOpen, Home, RefreshCw, X } from 'lucide-react';
import { API_BASE_URL } from '../../config';
import { fmtDate } from './formatters';
import { formToPayload } from './dataSourceHelpers';

function fmtSize(bytes) {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let n = bytes / 1024;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n < 10 ? 1 : 0)} ${units[i]}`;
}

function RemoteBrowserModal({
  open,
  step,
  form,
  passwordOverride,
  configuredSource,
  initialPath,
  getAuthHeaders,
  onClose,
  onSelect,
}) {
  const [currentPath, setCurrentPath] = useState(initialPath || '/');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (path) => {
    if (!step?.id) return;
    setLoading(true);
    setError('');
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const body = {
        ...formToPayload(form, { passwordOverride }),
        path,
      };
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/ahri/dashboard/data-sources/${step.id}/browse`,
        { method: 'POST', headers, body: JSON.stringify(body) },
      );
      const payload = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(payload.detail || `Browse failed (${resp.status})`);
      }
      const result = payload.result || {};
      if (!result.ok) {
        throw new Error(result.message || 'Unable to list remote directory');
      }
      setData(result);
      setCurrentPath(result.path || path);
    } catch (err) {
      setError(err.message || 'Browse failed');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [form, getAuthHeaders, step]);

  useEffect(() => {
    if (!open) return;
    setCurrentPath(initialPath || '/');
    load(initialPath || '/');
  }, [open, initialPath, load]);

  if (!open) return null;

  const handleEnter = (entry) => {
    if (entry.kind !== 'directory') return;
    const next = currentPath === '/' ? `/${entry.name}` : `${currentPath}/${entry.name}`;
    load(next);
  };

  const breadcrumbs = data?.breadcrumbs || [{ name: '/', path: '/' }];
  const entries = data?.entries || [];

  return (
    <>
      <div
        role="presentation"
        className="ahri-browser__backdrop"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Browse remote folder"
        className="ahri-browser__modal"
      >
        <header className="ahri-browser__header">
          <div className="d-flex align-items-center gap-2">
            <span className="ahri-browser__icon">
              <FolderOpen size={18} />
            </span>
            <div>
              <div className="ahri-browser__title">Browse remote folder</div>
              <div className="ahri-browser__subtitle">
                {form.protocol?.toUpperCase()} · {form.username}@{form.host}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={onClose}
            aria-label="Close browser"
          >
            <X size={16} />
          </button>
        </header>

        <div className="ahri-browser__toolbar">
          <nav aria-label="Path breadcrumbs" className="ahri-browser__breadcrumbs">
            {breadcrumbs.map((crumb, idx) => (
              <React.Fragment key={crumb.path}>
                <button
                  type="button"
                  className={`ahri-browser__crumb${idx === breadcrumbs.length - 1 ? ' is-current' : ''}`}
                  onClick={() => load(crumb.path)}
                  disabled={loading}
                >
                  {idx === 0 ? <Home size={12} className="me-1" /> : null}
                  {crumb.name === '/' ? 'root' : crumb.name}
                </button>
                {idx < breadcrumbs.length - 1 ? (
                  <ChevronRight size={12} className="ahri-browser__crumb-sep" />
                ) : null}
              </React.Fragment>
            ))}
          </nav>
          <button
            type="button"
            className="btn btn-sm btn-light d-inline-flex align-items-center gap-1"
            onClick={() => load(currentPath)}
            disabled={loading}
            title="Refresh"
          >
            <RefreshCw size={14} className={loading ? 'ahri-dash-spin' : ''} />
            Refresh
          </button>
        </div>

        {error ? (
          <div className="alert alert-danger py-2 mx-3 my-2" role="alert">{error}</div>
        ) : null}

        <div className="ahri-browser__list">
          {loading && !data ? (
            <div className="ahri-browser__empty">Loading…</div>
          ) : !entries.length ? (
            <div className="ahri-browser__empty">This folder is empty.</div>
          ) : (
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col" className="text-end" style={{ width: 110 }}>Size</th>
                  <th scope="col" style={{ width: 170 }}>Modified</th>
                </tr>
              </thead>
              <tbody>
                {data?.parent ? (
                  <tr
                    className="ahri-browser__row ahri-browser__row--parent"
                    onClick={() => load(data.parent)}
                  >
                    <td>
                      <Folder size={14} className="me-2 text-muted" />
                      .. (parent)
                    </td>
                    <td className="text-end text-muted">—</td>
                    <td className="text-muted">—</td>
                  </tr>
                ) : null}
                {entries.map((entry) => {
                  const isDir = entry.kind === 'directory';
                  return (
                    <tr
                      key={entry.name}
                      className={`ahri-browser__row${isDir ? ' ahri-browser__row--dir' : ' ahri-browser__row--file'}`}
                      onClick={isDir ? () => handleEnter(entry) : undefined}
                      role={isDir ? 'button' : undefined}
                    >
                      <td>
                        {isDir ? (
                          <Folder size={14} className="me-2 ahri-browser__icon-dir" />
                        ) : (
                          <FileIcon size={14} className="me-2 text-muted" />
                        )}
                        {entry.name}
                      </td>
                      <td className="text-end text-muted">{isDir ? '—' : fmtSize(entry.size)}</td>
                      <td className="text-muted">{entry.modified ? fmtDate(entry.modified) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <footer className="ahri-browser__footer">
          <div className="ahri-browser__current">
            <span className="text-muted me-1">Selected folder:</span>
            <code>{currentPath}</code>
          </div>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-sm btn-light" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={() => {
                onSelect?.(currentPath);
                onClose?.();
              }}
              disabled={loading}
            >
              Use this folder
            </button>
          </div>
        </footer>
      </div>
    </>
  );
}

export default RemoteBrowserModal;
